import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useNavigate } from "react-router-dom";
import axios from "../../axiosInstace";
import { toast } from "react-toastify";
import { useAuth } from "../../contexts/AuthContext";
import { useRolesAndDepartments } from "../../hooks/useCommonData";
import "./Vacancies.scss";
import VacancyDetailsDrawer from "./VacancyDetailsDrawer";
import AccessDenied from "../AccessDenied";
import { Icon } from "@iconify/react";
import { useWorkspace, WORKSPACES } from "../../contexts/Workspacecontext";
import IconTooltip from "./IconTooltip";
import LockedButton from "../../components/LockedButton";
import { RECRUITMENT } from "../../config/moduleAccess";

// ─── Constants ────────────────────────────────────────────────────────────────
const STEPS = ["Vacancy Details", "Job Details"];

const EMPLOYMENT_TYPES = [
  ["PERMANENT", "Permanent"],
  ["CONTRACT", "Contract"],
  ["PART_TIME", "Part Time"],
  ["INTERN", "Intern"],
];

const BUDGET_TYPES = [
  ["ANNUAL", "Annual"],
  ["MONTHLY", "Monthly"],
];

const SALARY_TYPES = [
  ["CTC", "CTC"],
  ["GROSS", "Gross"],
  ["NET", "Net"],
];

const VACANCY_REASON_SUGGESTIONS = [
  "Replacement for resigned employee",
  "Team expansion due to new project",
  "Backfill for internal transfer",
  "Urgent client delivery requirement",
  "New business vertical hiring",
];

const JOB_DESCRIPTION_SUGGESTIONS = [
  "Manage day-to-day job responsibilities efficiently",
  "Coordinate with internal teams and stakeholders",
  "Ensure timely completion of assigned tasks",
  "Maintain process quality and compliance standards",
  "Support business and operational objectives",
];

const DESIRED_PROFILE_SUGGESTIONS = [
  "Strong communication and interpersonal skills",
  "Ability to work independently and in teams",
  "Good problem-solving and decision-making abilities",
  "Adaptable and willing to learn new processes",
  "Organized and detail-oriented approach",
];

const RESPONSIBILITY_SUGGESTIONS = [
  "Handle assigned operational responsibilities",
  "Coordinate with departments for smooth workflow",
  "Maintain accurate records and documentation",
  "Follow company policies and procedures",
  "Support team and organizational goals",
];

const EXPERIENCE_OPTIONS = [
  { label: "Fresher", value: "Fresher (0 years)" },
  { label: "0–1 yr", value: "0–1 years" },
  { label: "1–2 yrs", value: "1–2 years" },
  { label: "2–3 yrs", value: "2–3 years" },
  { label: "3–5 yrs", value: "3–5 years" },
  { label: "5–7 yrs", value: "5–7 years" },
  { label: "7–10 yrs", value: "7–10 years" },
  { label: "10+ yrs", value: "10+ years" },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────
const toIndianFormat = (num) => {
  if (!num && num !== 0) return "";
  const str = String(Math.floor(Number(num)));
  if (str.length <= 3) return str;
  const last3 = str.slice(-3);
  const rest = str.slice(0, -3);
  return rest.replace(/\B(?=(\d{2})+(?!\d))/g, ",") + "," + last3;
};

const fromIndianFormat = (str) => str.replace(/,/g, "");

const extractQualificationIdsFromVacancy = (v) => {
  if (Array.isArray(v.qualifications) && v.qualifications.length > 0) {
    return v.qualifications.map((q) =>
      typeof q === "object" ? q.qualificationId : q,
    );
  }
  if (Array.isArray(v.qualificationIds)) return v.qualificationIds;
  return [];
};

// ─── useQualificationsLocal ───────────────────────────────────────────────────
const useQualificationsLocal = () => {
  const [qualifications, setQualifications] = useState([]);
  const [qualificationsLoading, setQualificationsLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_API_HR_URL}/job-post-qualification/all`)
      .then((r) => setQualifications(r.data.data || []))
      .catch(console.error)
      .finally(() => setQualificationsLoading(false));
  }, []);

  return { qualifications, qualificationsLoading };
};

// ─── QualificationMultiSelectLocal ───────────────────────────────────────────
const QualificationMultiSelectLocal = ({
  qualifications,
  qualificationsLoading,
  selectedIds,
  onChange,
  error,
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const containerRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target))
        setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return qualifications.filter(
      (item) =>
        item.qualificationName.toLowerCase().includes(q) ||
        item.qualificationType.toLowerCase().includes(q),
    );
  }, [qualifications, search]);

  const grouped = useMemo(
    () =>
      filtered.reduce((acc, item) => {
        const t = item.qualificationType;
        if (!acc[t]) acc[t] = [];
        acc[t].push(item);
        return acc;
      }, {}),
    [filtered],
  );

  const selectedItems = qualifications.filter((q) =>
    selectedIds.includes(q.qualificationId),
  );

  const toggle = (id) => {
    onChange(
      selectedIds.includes(id)
        ? selectedIds.filter((x) => x !== id)
        : [...selectedIds, id],
    );
  };

  const FRIENDLY_TYPE = {
    SCHOOL: "School",
    PRE_UNIVERSITY: "Pre-University",
    BACHELOR: "Bachelor's Degree",
    MASTER: "Master's Degree",
    PHD: "PhD / Doctorate",
    OTHER: "Other",
  };

  return (
    <div
      ref={containerRef}
      style={{ position: "relative" }}
      className={error ? "jp-qs--error" : ""}
    >
      <button
        type="button"
        className={`cv-field__input${error ? " cv-field__input--error" : ""}`}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          minHeight: 38,
          height: "auto",
          padding: "6px 10px",
          flexWrap: "wrap",
          gap: 4,
          cursor: qualificationsLoading ? "not-allowed" : "pointer",
          textAlign: "left",
          width: "100%",
          boxSizing: "border-box",
        }}
        onClick={() => !qualificationsLoading && setOpen((p) => !p)}
        disabled={qualificationsLoading}
      >
        <span style={{ display: "flex", flexWrap: "wrap", gap: 4, flex: 1 }}>
          {selectedItems.length === 0 ? (
            <span style={{ color: "#9ca3af", fontSize: 13 }}>
              {qualificationsLoading
                ? "Loading qualifications…"
                : "Select qualifications"}
            </span>
          ) : (
            selectedItems.map((item) => (
              <span
                key={item.qualificationId}
                style={{
                  background: "#eff6ff",
                  color: "#1d4ed8",
                  borderRadius: 4,
                  padding: "1px 6px",
                  fontSize: 11,
                  fontWeight: 500,
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                }}
              >
                {item.qualificationName}
                <span
                  role="button"
                  style={{ cursor: "pointer", lineHeight: 1 }}
                  onMouseDown={(e) => {
                    e.stopPropagation();
                    toggle(item.qualificationId);
                  }}
                >
                  ×
                </span>
              </span>
            ))
          )}
        </span>
        <svg
          width="12"
          height="12"
          viewBox="0 0 12 12"
          fill="none"
          style={{
            flexShrink: 0,
            transform: open ? "rotate(180deg)" : "none",
            transition: "transform 0.15s",
          }}
        >
          <path
            d="M2 4l4 4 4-4"
            stroke="#6b7280"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {open && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 4px)",
            left: 0,
            right: 0,
            zIndex: 9999,
            background: "#fff",
            border: "1px solid #d1d5db",
            borderRadius: 8,
            boxShadow: "0 4px 16px rgba(0,0,0,0.10)",
            maxHeight: 280,
            overflowY: "auto",
          }}
        >
          <div
            style={{
              padding: "8px 10px",
              borderBottom: "1px solid #f3f4f6",
              position: "sticky",
              top: 0,
              background: "#fff",
            }}
          >
            <input
              autoFocus
              className="cv-field__input"
              style={{ fontSize: 12, padding: "4px 8px" }}
              placeholder="Search qualifications…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onClick={(e) => e.stopPropagation()}
            />
          </div>

          {Object.keys(grouped).length === 0 ? (
            <div
              style={{
                padding: 12,
                fontSize: 13,
                color: "#9ca3af",
                textAlign: "center",
              }}
            >
              No qualifications found
            </div>
          ) : (
            Object.entries(grouped).map(([type, items]) => (
              <div key={type}>
                <div
                  style={{
                    padding: "6px 12px",
                    fontSize: 10,
                    fontWeight: 700,
                    color: "#6b7280",
                    letterSpacing: "0.06em",
                    background: "#f9fafb",
                    borderBottom: "1px solid #f3f4f6",
                    position: "sticky",
                    top: 44,
                  }}
                >
                  {FRIENDLY_TYPE[type] || type}
                </div>
                {items.map((item) => {
                  const checked = selectedIds.includes(item.qualificationId);
                  return (
                    <label
                      key={item.qualificationId}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        padding: "7px 12px",
                        cursor: "pointer",
                        fontSize: 13,
                        background: checked ? "#eff6ff" : "transparent",
                        borderBottom: "1px solid #f3f4f6",
                        transition: "background 0.1s",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggle(item.qualificationId)}
                        style={{ accentColor: "#2563eb" }}
                      />
                      <span
                        style={{ flex: 1, fontWeight: checked ? 600 : 400 }}
                      >
                        {item.qualificationName}
                      </span>
                      {checked && (
                        <svg
                          width="13"
                          height="13"
                          viewBox="0 0 13 13"
                          fill="none"
                        >
                          <path
                            d="M2.5 6.5l3 3 5-5"
                            stroke="#2563eb"
                            strokeWidth="1.8"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      )}
                    </label>
                  );
                })}
              </div>
            ))
          )}
        </div>
      )}

      {error && (
        <span
          className="cv-field__error"
          style={{ marginTop: 4, display: "block" }}
        >
          {error}
        </span>
      )}
    </div>
  );
};

// ─── initialForm ──────────────────────────────────────────────────────────────
const initialForm = {
  roleId: "",
  jobTitle: "",
  employmentType: "PERMANENT",
  qualificationIds: [],
  noOfVacancy: 1,
  budget: "",
  budgetDisplay: "",
  budgetType: "ANNUAL",
  salaryType: "CTC",
  expectedClosingTime: "",
  reasonOfVacancy: "",
  branchId: "",
  departmentId: "",
  jobDescription: "",
  responsibilities: "",
  desiredProfile: "",
  experience: "",
  skills: "",
};

// ─── Field wrapper ────────────────────────────────────────────────────────────
const F = ({ label, required, error, children, hint }) => (
  <div className={`cv-field${error ? " cv-field--error" : ""}`}>
    <label className="cv-field__label">
      {label}
      {required && <span className="cv-field__req"> *</span>}
      {hint && <span className="cv-field__hint"> {hint}</span>}
    </label>
    {children}
    {error && <span className="cv-field__error">{error}</span>}
  </div>
);

// ─── fmtDate ──────────────────────────────────────────────────────────────────
const fmtDate = (iso) => {
  if (!iso) return null;
  return new Date(iso).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

// ─── Lazy-load approval pages ─────────────────────────────────────────────────
const HodApprovals = React.lazy(() => import("./HodApprovals"));
const HrApprovals = React.lazy(() => import("./HrApprovals"));

// ─── Entry point ──────────────────────────────────────────────────────────────
const AllVacancies = () => {
  const { hasRole } = useAuth();
  const isHR = hasRole("ROLE_RECRUITER-HR");
  const isHOD = hasRole("ROLE_HOD");
  const isManager = hasRole("ROLE_MANAGER");

  const views = useMemo(() => {
    const arr = [];
    if (isManager) arr.push({ key: "my", label: "My Vacancies" });
    if (isHOD) arr.push({ key: "hod", label: "HOD Approvals" });
    if (isHR) arr.push({ key: "hr", label: "HR Approvals" });
    return arr;
  }, [isManager, isHOD, isHR]);

  const getDefaultView = () => {
    if (isManager) return "my";
    if (isHOD) return "hod";
    if (isHR) return "hr";
    return "my";
  };

  const [view, setView] = React.useState(() => {
    const saved = localStorage.getItem("vacancy-view-mode");
    const allowedViews = views.map((v) => v.key);
    if (saved && allowedViews.includes(saved)) return saved;
    return getDefaultView();
  });

  useEffect(() => {
    const allowedViews = views.map((v) => v.key);
    if (allowedViews.includes(view))
      localStorage.setItem("vacancy-view-mode", view);
  }, [view, views]);

  if (!isHR && !isHOD && !isManager) return <AccessDenied />;

  return (
    <div>
      {views.length > 1 && (
        <div className="vacancy-view-toggle">
          {views.map((v) => (
            <button
              key={v.key}
              className={`vacancy-view-toggle__btn${
                view === v.key ? " vacancy-view-toggle__btn--active" : ""
              }`}
              onClick={() => setView(v.key)}
            >
              {v.key === "my" && (
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <rect
                    x="1"
                    y="1"
                    width="12"
                    height="12"
                    rx="2"
                    stroke="currentColor"
                    strokeWidth="1.4"
                  />
                  <path
                    d="M7 4v6M4 7h6"
                    stroke="currentColor"
                    strokeWidth="1.4"
                    strokeLinecap="round"
                  />
                </svg>
              )}
              {v.key === "hod" && (
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path
                    d="M2 4h10M2 7h7M2 10h5"
                    stroke="currentColor"
                    strokeWidth="1.4"
                    strokeLinecap="round"
                  />
                </svg>
              )}
              {v.key === "hr" && (
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path
                    d="M2 4h10M2 7h10M2 10h7"
                    stroke="currentColor"
                    strokeWidth="1.4"
                    strokeLinecap="round"
                  />
                </svg>
              )}
              {v.label}
            </button>
          ))}
        </div>
      )}

      {view === "my" && <ManagerView />}
      {view === "hod" && (
        <React.Suspense
          fallback={<div className="vacancy-loader">Loading...</div>}
        >
          <HodApprovals />
        </React.Suspense>
      )}
      {view === "hr" && (
        <React.Suspense
          fallback={<div className="vacancy-loader">Loading...</div>}
        >
          <HrApprovals />
        </React.Suspense>
      )}
    </div>
  );
};

// ─── DonutChart ───────────────────────────────────────────────────────────────
const DonutChart = ({ segments, size = 72 }) => {
  const r = (size - 12) / 2;
  const cx = size / 2;
  const total = segments.reduce((s, seg) => s + seg.value, 0);
  if (total === 0) {
    return (
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle cx={cx} cy={cx} r={r} fill="#f3f4f6" />
        <circle cx={cx} cy={cx} r={r * 0.58} fill="white" />
        <text
          x={cx}
          y={cx + 4}
          textAnchor="middle"
          fontSize="11"
          fontWeight="600"
          fill="#9ca3af"
        >
          0
        </text>
      </svg>
    );
  }
  let cumulative = 0;
  const slices = segments
    .filter((s) => s.value > 0)
    .map((seg) => {
      const pct = seg.value / total;
      const start = cumulative;
      cumulative += pct;
      const startAngle = start * 2 * Math.PI - Math.PI / 2;
      const endAngle = cumulative * 2 * Math.PI - Math.PI / 2;
      const x1 = cx + r * Math.cos(startAngle);
      const y1 = cx + r * Math.sin(startAngle);
      const x2 = cx + r * Math.cos(endAngle);
      const y2 = cx + r * Math.sin(endAngle);
      const large = pct > 0.5 ? 1 : 0;
      return {
        ...seg,
        d: `M ${cx} ${cx} L ${x1} ${y1} A ${r} ${r} 0 ${large} 1 ${x2} ${y2} Z`,
      };
    });
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      {slices.map((s, i) => (
        <path key={i} d={s.d} fill={s.color} opacity="0.9" />
      ))}
      <circle cx={cx} cy={cx} r={r * 0.58} fill="white" />
      <text
        x={cx}
        y={cx + 4}
        textAnchor="middle"
        fontSize="11"
        fontWeight="600"
        fill="#1e293b"
      >
        {total}
      </text>
    </svg>
  );
};

// ─── ManagerSidebar ───────────────────────────────────────────────────────────
const ManagerSidebar = ({ vacancies }) => {
  const total = vacancies.length;

  const approvalCounts = vacancies.reduce(
    (acc, v) => {
      const hr = (v.HRApprovalStatus || v.hrapprovalStatus || "").toUpperCase();
      const hod = (
        v.HODApprovalStatus ||
        v.hodapprovalStatus ||
        ""
      ).toUpperCase();
      if (hr === "APPROVED" && hod === "APPROVED") acc.fullyApproved++;
      else if (hr === "REJECTED" || hod === "REJECTED") acc.rejected++;
      else if (hr === "APPROVED" || hod === "APPROVED") acc.partiallyApproved++;
      else acc.pending++;
      return acc;
    },
    { fullyApproved: 0, partiallyApproved: 0, pending: 0, rejected: 0 },
  );

  const typeCounts = vacancies.reduce((acc, v) => {
    const t = (v.employmentType || "UNKNOWN").toUpperCase().replace(/\s/g, "_");
    acc[t] = (acc[t] || 0) + 1;
    return acc;
  }, {});

  const now = new Date();
  const urgencyBuckets = vacancies.reduce(
    (acc, v) => {
      if (!v.expectedClosingTime) {
        acc.noDate++;
        return acc;
      }
      const diff = Math.ceil(
        (new Date(v.expectedClosingTime) - now) / (1000 * 60 * 60 * 24),
      );
      if (diff < 0) acc.overdue++;
      else if (diff <= 3) acc.urgent++;
      else if (diff <= 7) acc.soon++;
      else acc.ok++;
      return acc;
    },
    { overdue: 0, urgent: 0, soon: 0, ok: 0, noDate: 0 },
  );

  const deptCounts = vacancies.reduce((acc, v) => {
    const d = v.departmentName || "Unknown";
    acc[d] = (acc[d] || 0) + 1;
    return acc;
  }, {});
  const deptEntries = Object.entries(deptCounts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 6);
  const deptMax = Math.max(...deptEntries.map(([, n]) => n), 1);

  return (
    <aside className="hfd-sidebar">
      <div className="hfd-widget">
        <p className="hfd-widget__title">Approval status</p>
        <div className="hfd-donut-wrap">
          <DonutChart
            size={84}
            segments={[
              { value: approvalCounts.fullyApproved, color: "#0f6e56" },
              { value: approvalCounts.partiallyApproved, color: "#2563eb" },
              { value: approvalCounts.pending, color: "#f59e0b" },
              { value: approvalCounts.rejected, color: "#dc2626" },
            ]}
          />
          <div className="hfd-donut-legend">
            {[
              {
                label: "Fully approved",
                val: approvalCounts.fullyApproved,
                color: "#0f6e56",
              },
              {
                label: "Partial",
                val: approvalCounts.partiallyApproved,
                color: "#2563eb",
              },
              {
                label: "Pending",
                val: approvalCounts.pending,
                color: "#f59e0b",
              },
              {
                label: "Rejected",
                val: approvalCounts.rejected,
                color: "#dc2626",
              },
            ].map(({ label, val, color }) => (
              <div key={label} className="hfd-donut-legend__row">
                <span
                  className="hfd-donut-legend__dot"
                  style={{ background: color }}
                />
                <span className="hfd-donut-legend__label">{label}</span>
                <span className="hfd-donut-legend__val">{val}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="hfd-widget">
        <p className="hfd-widget__title">Closing urgency</p>
        <div className="hfd-sparkbars">
          {[
            {
              label: "Overdue",
              val: urgencyBuckets.overdue,
              color: "#dc2626",
            },
            {
              label: "≤ 3 days",
              val: urgencyBuckets.urgent,
              color: "#f59e0b",
            },
            { label: "≤ 7 days", val: urgencyBuckets.soon, color: "#2563eb" },
            { label: "On track", val: urgencyBuckets.ok, color: "#0f6e56" },
            {
              label: "No date",
              val: urgencyBuckets.noDate,
              color: "#9ca3af",
            },
          ].map(({ label, val, color }) => {
            const pct = (val / Math.max(total, 1)) * 100;
            return (
              <div key={label} className="hfd-spark-row">
                <span className="hfd-spark-row__label">{label}</span>
                <div className="hfd-spark-row__track">
                  <div
                    className="hfd-spark-row__fill"
                    style={{ width: `${pct}%`, background: color }}
                  />
                </div>
                <span className="hfd-spark-row__val">{val}</span>
              </div>
            );
          })}
        </div>
      </div>

      <div className="hfd-widget">
        <p className="hfd-widget__title">By department</p>
        <div className="hfd-sparkbars">
          {deptEntries.map(([dept, count]) => (
            <div key={dept} className="hfd-spark-row">
              <span className="hfd-spark-row__label" title={dept}>
                {dept.length > 18 ? dept.slice(0, 17) + "…" : dept}
              </span>
              <div className="hfd-spark-row__track">
                <div
                  className="hfd-spark-row__fill"
                  style={{ width: `${(count / deptMax) * 100}%` }}
                />
              </div>
              <span className="hfd-spark-row__val">{count}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="hfd-widget">
        <p className="hfd-widget__title">Employment type</p>
        <div className="hfd-donut-wrap">
          <DonutChart
            size={84}
            segments={[
              { value: typeCounts.PERMANENT || 0, color: "#2563eb" },
              { value: typeCounts.CONTRACT || 0, color: "#0f6e56" },
              { value: typeCounts.PART_TIME || 0, color: "#f59e0b" },
              { value: typeCounts.INTERN || 0, color: "#6d28d9" },
            ]}
          />
          <div className="hfd-donut-legend">
            {[
              {
                label: "Permanent",
                val: typeCounts.PERMANENT || 0,
                color: "#2563eb",
              },
              {
                label: "Contract",
                val: typeCounts.CONTRACT || 0,
                color: "#0f6e56",
              },
              {
                label: "Part-time",
                val: typeCounts.PART_TIME || 0,
                color: "#f59e0b",
              },
              {
                label: "Intern",
                val: typeCounts.INTERN || 0,
                color: "#6d28d9",
              },
            ].map(({ label, val, color }) => (
              <div key={label} className="hfd-donut-legend__row">
                <span
                  className="hfd-donut-legend__dot"
                  style={{ background: color }}
                />
                <span className="hfd-donut-legend__label">{label}</span>
                <span className="hfd-donut-legend__val">{val}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="hfd-widget hfd-widget--tips">
        <p className="hfd-widget__title">Quick tips</p>
        <ul className="hfd-tips">
          <li>
            Approved vacancies <strong>cannot be edited</strong> — raise a new
            vacancy if changes are needed.
          </li>
          <li>
            Create a <strong>Job Post</strong> once HR approves to start
            receiving applications.
          </li>
          <li>
            Set an expected closing date so HODs can prioritize approvals.
          </li>
        </ul>
      </div>
    </aside>
  );
};

// ─── ApprovalBadge ────────────────────────────────────────────────────────────
const ApprovalBadge = ({ type, status }) => {
  const normalized = (status || "").toUpperCase();
  const config = {
    APPROVED: {
      className: "badge badge--approved",
      label: "Approved",
      dot: "🟢",
    },
    PENDING: {
      className: "badge badge--pending",
      label: "Pending",
      dot: "🟡",
    },
    REJECTED: {
      className: "badge badge--rejected",
      label: "Rejected",
      dot: "🔴",
    },
  };
  const current = config[normalized] || {
    className: "badge badge--default",
    label: status || "Unknown",
    dot: "⚪",
  };
  return (
    <span className={current.className}>
      <span style={{ fontSize: 10 }}>{current.dot}</span>
      {type}: {current.label}
    </span>
  );
};

// ─── ManagerView ──────────────────────────────────────────────────────────────
const ManagerView = () => {
  const navigate = useNavigate();
  const [vacancies, setVacancies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editVacancy, setEditVacancy] = useState(null);
  const [detailVacancy, setDetailVacancy] = useState(null);
  const { hasRole } = useAuth();
  const isManager = hasRole("ROLE_MANAGER");
  const { workspace } = useWorkspace();
  const routePrefix =
    workspace === WORKSPACES.CONSULTANT ? "/consultant" : "/recruitment";

  const fetchVacancies = useCallback(() => {
    setLoading(true);
    axios
      .get(
        `${process.env.REACT_APP_API_MANAGER_URL}/recruitment/vacancies/all/manager`,
      )
      .then((res) => setVacancies(res.data.data || []))
      .catch((err) => console.error(err))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchVacancies();
  }, [fetchVacancies]);

  const goCreateJobPost = (vacancy) => {
    setDetailVacancy(null);
    navigate("/recruitment/job-posts/all", { state: { vacancy } });
  };

  const handleEdit = (vacancy) => {
    setDetailVacancy(null);
    setEditVacancy(vacancy);
  };

  const handleDelete = async (vacancy) => {
    try {
      await axios.delete(
        `${process.env.REACT_APP_API_MANAGER_URL}/recruitment/vacancies/${vacancy.vacancyId}`,
      );
      setDetailVacancy(null);
      fetchVacancies();
      toast.success("Vacancy deleted.");
    } catch (err) {
      const status = err?.response?.status;
      const msg = err?.response?.data?.message || "";
      if (status === 409 && msg) toast.error(msg);
      else toast.error("Something went wrong, please try again.");
    }
  };

  const isRecentClosing = (dateStr) => {
    if (!dateStr) return false;
    const closingDate = new Date(dateStr);
    const now = new Date();
    const diffDays = Math.ceil((closingDate - now) / (1000 * 60 * 60 * 24));
    if (diffDays <= 3 && diffDays >= 0) return { urgent: true, days: diffDays };
    return { urgent: false, days: diffDays };
  };

  const linkedJobPosts = vacancies.filter((v) => !!v.jobPostId).length;
  const urgentClosings = vacancies.filter(
    (v) =>
      v.expectedClosingTime && isRecentClosing(v.expectedClosingTime).urgent,
  ).length;
  const approvedVacancies = vacancies.filter(
    (v) =>
      (v.HODApprovalStatus || "").toUpperCase() === "APPROVED" ||
      (v.HRApprovalStatus || "").toUpperCase() === "APPROVED",
  ).length;

  return (
    <div className="vacancy-page">
      <div className="vacancy-page__header">
        <div className="vacancy-page__header-main">
          <div className="vacancy-page__title-wrap">
            <h2 className="vacancy-page__title">
              My Vacancies (Man power requisition)
            </h2>
            <span className="vacancy-page__count">
              {vacancies.length} vacancies
            </span>
          </div>
          <p className="vacancy-page__sub">
            Track approvals, linked job posts, and vacancy urgency in one place
          </p>
        </div>
        {isManager && (
          <LockedButton
            codes={[RECRUITMENT.VACANCIES_CREATE]}
            className="av-create-btn"
            onClick={() => setDrawerOpen(true)}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path
                d="M8 2v12M2 8h12"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
            Create Vacancy
          </LockedButton>
        )}
      </div>

      <div className="vacancy-stats-row">
        <div className="vacancy-stat" style={{ "--accent": "#2563eb" }}>
          <div className="vacancy-stat__bar" />
          <div className="vacancy-stat__body">
            <div className="vacancy-stat__text">
              <p className="vacancy-stat__label">Total Vacancies</p>
              <p className="vacancy-stat__sub">Active request pipeline</p>
            </div>
            <p className="vacancy-stat__value">{vacancies.length}</p>
          </div>
        </div>
        <div className="vacancy-stat" style={{ "--accent": "#0f6e56" }}>
          <div className="vacancy-stat__bar" />
          <div className="vacancy-stat__body">
            <div className="vacancy-stat__text">
              <p className="vacancy-stat__label">Approved</p>
              <p className="vacancy-stat__sub">Ready for job post creation</p>
            </div>
            <p className="vacancy-stat__value">{approvedVacancies}</p>
          </div>
        </div>
        <div className="vacancy-stat" style={{ "--accent": "#d97706" }}>
          <div className="vacancy-stat__bar" />
          <div className="vacancy-stat__body">
            <div className="vacancy-stat__text">
              <p className="vacancy-stat__label">Urgent Closings</p>
              <p className="vacancy-stat__sub">Due within 3 days</p>
            </div>
            <p className="vacancy-stat__value">{urgentClosings}</p>
          </div>
        </div>
        <div className="vacancy-stat" style={{ "--accent": "#6d28d9" }}>
          <div className="vacancy-stat__bar" />
          <div className="vacancy-stat__body">
            <div className="vacancy-stat__text">
              <p className="vacancy-stat__label">Linked Job Posts</p>
              <p className="vacancy-stat__sub">Already pushed live</p>
            </div>
            <p className="vacancy-stat__value">{linkedJobPosts}</p>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="vacancy-loader">Loading vacancies...</div>
      ) : vacancies.length === 0 ? (
        <div className="vacancy-empty">
          <div className="vacancy-empty__icon">📋</div>
          <p>No vacancies found.</p>
          <LockedButton
            codes={[RECRUITMENT.VACANCIES_CREATE]}
            className="av-create-btn"
            onClick={() => setDrawerOpen(true)}
          >
            Create your first vacancy
          </LockedButton>
        </div>
      ) : (
        <div className="hfd-flow-grid">
          <div className="hfd-flow-list vacancy-list">
            {vacancies.map((v) => {
              const recent = isRecentClosing(v.expectedClosingTime);
              return (
                <div key={v.vacancyId} className="vacancy-card">
                  <div className="vacancy-card__left">
                    <div className="vacancy-card__title-line">
                      <h3 className="vacancy-card__title">{v.jobTitle}</h3>
                      <ApprovalBadge type="HR" status={v.HRApprovalStatus} />
                      <ApprovalBadge type="HOD" status={v.HODApprovalStatus} />
                      <ApprovalBadge type="Vacancy" status={v.vacancyStatus} />
                    </div>
                    <div className="vacancy-card__meta">
                      <div className="vacancy-meta-row">
                        {v.departmentName && (
                          <span className="vacancy-chip">
                            🏢 {v.departmentName}
                          </span>
                        )}

                        {v.roleName && (
                          <span className="vacancy-chip">🎯 {v.roleName}</span>
                        )}

                        {v.employmentType && (
                          <span className="vacancy-chip">
                            💼 {v.employmentType.replace(/_/g, " ")}
                          </span>
                        )}

                        <span className="vacancy-chip">
                          👥 {v.noOfVacancy} Positions
                        </span>
                      </div>

                      <div className="vacancy-meta-row">
                        {v.vacancyRequestedEmpName && (
                          <span className="vacancy-chip">
                            👤 Created by: {v.vacancyRequestedEmpName}
                          </span>
                        )}

                        {v.expectedClosingTime && (
                          <span
                            className={`vacancy-chip ${
                              recent.urgent ? "vacancy-chip--urgent" : ""
                            }`}
                          >
                            ⏳ Closes: {fmtDate(v.expectedClosingTime)}
                          </span>
                        )}
                      </div>
                    </div>
                    {v.reasonOfVacancy && (
                      <p className="vacancy-card__reason">
                        {v.reasonOfVacancy}
                      </p>
                    )}
                  </div>
                  <div className="vacancy-card__actions">
                    <IconTooltip label="Details">
                      <button
                        className="av-action-btn av-action-btn--remark"
                        onClick={() => setDetailVacancy(v)}
                      >
                        📋 Details
                      </button>
                    </IconTooltip>

                    {v.jobPostId && (
                      <IconTooltip label="Job Post">
                        <button
                          className="av-action-btn av-action-btn--view-jp"
                          onClick={() =>
                            navigate("/recruitment/job-posts/all", {
                              state: { highlightJobPostId: v.jobPostId },
                            })
                          }
                        >
                          🔗 Job Post
                        </button>
                      </IconTooltip>
                    )}

                    {(() => {
                      const isApproved =
                        (v.vacancyStatus || "").toUpperCase() === "APPROVED";

                      return (
                        <IconTooltip
                          label={
                            isApproved
                              ? "Approved vacancies cannot be edited"
                              : "Edit"
                          }
                        >
                          <button
                            className={`av-action-btn av-action-btn--icon av-action-btn--edit${
                              isApproved ? " av-action-btn--disabled" : ""
                            }`}
                            onClick={() => {
                              if (!isApproved) handleEdit(v);
                            }}
                          >
                            <Icon icon="tabler:edit" width={15} />
                          </button>
                        </IconTooltip>
                      );
                    })()}

                    <IconTooltip label="Candidates">
                      <button
                        className="av-action-btn av-action-btn--icon av-action-btn--candidates"
                        onClick={() =>
                          navigate("/recruitment/candidates/all", {
                            state: {
                              jobPostId: v.jobPostId,
                              jobTitle: v.jobTitle,
                            },
                          })
                        }
                      >
                        <Icon icon="tabler:users-group" width={16} />
                      </button>
                    </IconTooltip>

                    <IconTooltip label="Interviews">
                      <button
                        className="av-action-btn av-action-btn--icon av-action-btn--interviews"
                        onClick={() =>
                          navigate("/recruitment/interviews", {
                            state: {
                              jobPostId: v.jobPostId,
                              jobTitle: v.jobTitle,
                            },
                          })
                        }
                      >
                        <Icon icon="fluent:person-chat-24-regular" width={16} />
                      </button>
                    </IconTooltip>

                    <IconTooltip label="Job Posts">
                      <button
                        className="av-action-btn av-action-btn--icon"
                        onClick={() =>
                          navigate(`${routePrefix}/job-posts/all`, {
                            state: {
                              jobPostId: v.jobPostId,
                              jobTitle: v.jobTitle,
                            },
                          })
                        }
                        style={{ color: "#6d28d9" }}
                      >
                        <Icon icon="mdi:briefcase-outline" width={16} />
                      </button>
                    </IconTooltip>

                    <IconTooltip label="Delete">
                      <button
                        className="av-action-btn av-action-btn--icon av-action-btn--delete"
                        onClick={() => handleDelete(v)}
                      >
                        <Icon icon="tabler:trash" width={15} />
                      </button>
                    </IconTooltip>
                  </div>
                </div>
              );
            })}
          </div>
          <ManagerSidebar vacancies={vacancies} />
        </div>
      )}

      {drawerOpen && isManager && (
        <CreateVacancyDrawer
          onClose={() => setDrawerOpen(false)}
          onCreated={() => {
            setDrawerOpen(false);
            fetchVacancies();
            toast.success("Vacancy submitted for approval!");
          }}
        />
      )}

      {editVacancy && isManager && (
        <CreateVacancyDrawer
          editMode
          existingVacancy={editVacancy}
          onClose={() => setEditVacancy(null)}
          onCreated={() => {
            setEditVacancy(null);
            fetchVacancies();
            toast.success("Vacancy updated!");
          }}
        />
      )}

      {detailVacancy && (
        <VacancyDetailsDrawer
          vacancy={detailVacancy}
          onClose={() => setDetailVacancy(null)}
          onCreateJobPost={() => goCreateJobPost(detailVacancy)}
        />
      )}
    </div>
  );
};

// ─── DateTimePicker ───────────────────────────────────────────────────────────
const DateTimePicker = ({ value, onChange, error, name }) => {
  const datePart = value ? value.split("T")[0] : "";
  const timePart = value ? value.split("T")[1] || "10:00" : "10:00";
  const hours = timePart.split(":")[0] || "10";
  const minutes = timePart.split(":")[1] || "00";

  const QUICK_TIMES = [
    { label: "9 AM", h: "09", m: "00" },
    { label: "10 AM", h: "10", m: "00" },
    { label: "11 AM", h: "11", m: "00" },
    { label: "12 PM", h: "12", m: "00" },
    { label: "2 PM", h: "14", m: "00" },
    { label: "5 PM", h: "17", m: "00" },
  ];

  const emitChange = (d, h, m) => {
    if (!d) return;
    onChange({ target: { name, value: `${d}T${h}:${m}` } });
  };

  const QUICK_DATES = [
    { label: "+7 days", days: 7 },
    { label: "+15 days", days: 15 },
    { label: "+30 days", days: 30 },
    { label: "+60 days", days: 60 },
    { label: "+90 days", days: 90 },
  ];

  const applyQuickDate = (days) => {
    const d = new Date();
    d.setDate(d.getDate() + days);
    const dateStr = d.toISOString().split("T")[0];
    emitChange(dateStr, hours, minutes);
  };

  const currentHourStr = `${String(hours).padStart(2, "0")}:${String(
    minutes,
  ).padStart(2, "0")}`;

  const isActiveTime = (h, m) =>
    h === String(hours).padStart(2, "0") &&
    m === String(minutes).padStart(2, "0");

  return (
    <div className={`dtp-wrap${error ? " dtp-wrap--error" : ""}`}>
      <div className="dtp-row">
        <div className="dtp-date-wrap">
          <svg
            className="dtp-icon"
            width="15"
            height="15"
            viewBox="0 0 15 15"
            fill="none"
          >
            <rect
              x="1"
              y="2"
              width="13"
              height="12"
              rx="2"
              stroke="currentColor"
              strokeWidth="1.4"
            />
            <path
              d="M1 6h13M5 1v2M10 1v2"
              stroke="currentColor"
              strokeWidth="1.4"
              strokeLinecap="round"
            />
          </svg>
          <input
            type="date"
            className="dtp-date"
            value={datePart}
            onChange={(e) => emitChange(e.target.value, hours, minutes)}
            min={new Date().toISOString().split("T")[0]}
          />
        </div>
        <div className="dtp-time-wrap">
          <svg
            className="dtp-icon"
            width="15"
            height="15"
            viewBox="0 0 15 15"
            fill="none"
          >
            <circle
              cx="7.5"
              cy="7.5"
              r="6"
              stroke="currentColor"
              strokeWidth="1.4"
            />
            <path
              d="M7.5 4v3.5l2 2"
              stroke="currentColor"
              strokeWidth="1.4"
              strokeLinecap="round"
            />
          </svg>
          <input
            type="time"
            className="dtp-time"
            value={currentHourStr}
            onChange={(e) => {
              const [h, m] = e.target.value.split(":");
              emitChange(datePart, h, m);
            }}
          />
        </div>
      </div>

      <div className="dtp-chips">
        {QUICK_DATES.map((q) => (
          <button
            key={q.days}
            type="button"
            className="dtp-chip"
            onClick={() => applyQuickDate(q.days)}
          >
            {q.label}
          </button>
        ))}
      </div>

      <div className="dtp-chips">
        {QUICK_TIMES.map((t) => (
          <button
            key={t.label}
            type="button"
            className={`dtp-chip${
              isActiveTime(t.h, t.m) ? " dtp-chip--active" : ""
            }`}
            onClick={() =>
              emitChange(
                datePart || new Date().toISOString().split("T")[0],
                t.h,
                t.m,
              )
            }
          >
            {t.label}
          </button>
        ))}
      </div>

      {datePart && (
        <div className="dtp-preview">
          📅{" "}
          {new Date(`${datePart}T${hours}:${minutes}`).toLocaleString("en-IN", {
            weekday: "short",
            day: "2-digit",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
          })}
        </div>
      )}
    </div>
  );
};

// ─── BudgetInput ──────────────────────────────────────────────────────────────
const BudgetInput = ({ value, display, budgetType, salaryType, onChange }) => {
  const handleRawChange = (e) => {
    const raw = fromIndianFormat(e.target.value).replace(/[^\d]/g, "");
    const formatted = raw ? toIndianFormat(raw) : "";
    onChange({ budget: raw, budgetDisplay: formatted });
  };

  return (
    <div className="budget-wrap">
      <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
        <div
          className="budget-input-row"
          style={{ flex: 1, position: "relative" }}
        >
          <span className="budget-rupee">₹</span>
          <input
            className="cv-field__input budget-input"
            value={display}
            onChange={handleRawChange}
            placeholder="e.g. 8,00,000"
            inputMode="numeric"
            style={{ paddingRight: "90px" }}
          />
          <div
            style={{
              position: "absolute",
              right: "8px",
              top: "50%",
              transform: "translateY(-50%)",
              display: "flex",
              gap: "4px",
            }}
          >
            {BUDGET_TYPES.map(([val, label]) => (
              <button
                key={val}
                type="button"
                className={`budget-type-btn${
                  budgetType === val ? " budget-type-btn--active" : ""
                }`}
                onClick={() => onChange({ budgetType: val })}
                style={{ padding: "2px 6px", fontSize: "11px" }}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
        <div
          style={{
            display: "flex",
            gap: "6px",
            alignItems: "center",
            whiteSpace: "nowrap",
          }}
        >
          <span style={{ fontSize: 12, color: "#6b7280" }}>Salary type:</span>
          {SALARY_TYPES.map(([val, label]) => (
            <button
              key={val}
              type="button"
              className={`budget-type-btn${
                salaryType === val ? " budget-type-btn--active" : ""
              }`}
              onClick={() => onChange({ salaryType: val })}
              style={{ padding: "4px 8px", fontSize: "12px" }}
            >
              {label}
            </button>
          ))}
        </div>
      </div>
      {display && (
        <div className="budget-preview">
          ₹{display}{" "}
          {BUDGET_TYPES.find((b) => b[0] === budgetType)?.[1].toLowerCase() ||
            ""}
          {" · "}
          {SALARY_TYPES.find((s) => s[0] === salaryType)?.[1] || ""}
          {budgetType === "MONTHLY" && value
            ? ` (₹${toIndianFormat(Number(value) * 12)} annual)`
            : ""}
        </div>
      )}
    </div>
  );
};

// ─── ExperienceCards ──────────────────────────────────────────────────────────
const ExperienceCards = ({ value, onChange }) => {
  const matchedOption = EXPERIENCE_OPTIONS.find((o) => o.value === value);
  const selectedPill = matchedOption ? value : null;
  const inputVal = value || "";

  return (
    <div className="exp-wrap">
      <div className="exp-cards">
        {EXPERIENCE_OPTIONS.map((opt) => (
          <button
            key={opt.value}
            type="button"
            className={`exp-card${
              selectedPill === opt.value ? " exp-card--active" : ""
            }`}
            onClick={() =>
              onChange({ target: { name: "experience", value: opt.value } })
            }
          >
            {opt.label}
          </button>
        ))}
      </div>
      <div style={{ marginTop: 8 }}>
        <input
          className="cv-field__input"
          placeholder="Or enter custom experience (e.g. '4–6 years in fintech')"
          value={inputVal}
          onChange={(e) =>
            onChange({ target: { name: "experience", value: e.target.value } })
          }
        />
      </div>
    </div>
  );
};

// ─── CreateVacancyDrawer ──────────────────────────────────────────────────────
const CreateVacancyDrawer = ({
  onClose,
  onCreated,
  editMode = false,
  existingVacancy = null,
}) => {
  const navigate = useNavigate();
  const { roles, departments, loading: metaLoading } = useRolesAndDepartments();
  const { qualifications, qualificationsLoading } = useQualificationsLocal();

  const [branches, setBranches] = useState([]);
  const [branchesLoading, setBranchesLoading] = useState(true);
  const [step, setStep] = useState(0);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const [form, setForm] = useState(() => {
    if (editMode && existingVacancy) {
      const ev = existingVacancy;
      const closingRaw = ev.expectedClosingTime
        ? new Date(ev.expectedClosingTime).toISOString().slice(0, 16)
        : "";
      return {
        ...initialForm,
        roleId: String(ev.roleId || ""),
        jobTitle: ev.jobTitle || "",
        employmentType: ev.employmentType
          ? ev.employmentType.toUpperCase().replace(/\s/g, "_")
          : "PERMANENT",
        qualificationIds: extractQualificationIdsFromVacancy(ev),
        noOfVacancy: ev.noOfVacancy || 1,
        budget: String(ev.budget || ""),
        budgetDisplay: ev.budget ? toIndianFormat(ev.budget) : "",
        budgetType: ev.budgetType ? ev.budgetType.toUpperCase() : "ANNUAL",
        salaryType: ev.salaryType ? ev.salaryType.toUpperCase() : "CTC",
        expectedClosingTime: closingRaw,
        reasonOfVacancy: ev.reasonOfVacancy || "",
        branchId: String(ev.branchId || ""),
        departmentId: String(ev.departmentId || ""),
        jobDescription: ev.jobDescription || "",
        responsibilities: ev.responsibilities || "",
        desiredProfile: ev.desiredProfile || "",
        experience: ev.experience || "",
        skills: ev.skills || "",
      };
    }
    return { ...initialForm };
  });

  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_COMMON_API_URL}/branches/list`)
      .then((r) => setBranches(r.data.data || []))
      .catch(console.error)
      .finally(() => setBranchesLoading(false));
  }, []);

  const [titleAutoFilled, setTitleAutoFilled] = useState(!editMode);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => {
      const updated = { ...prev, [name]: value };
      if (name === "roleId" && titleAutoFilled) {
        const role = roles.find((r) => String(r.roleId) === String(value));
        if (role) updated.jobTitle = role.roleName;
      }
      if (name === "jobTitle") setTitleAutoFilled(false);
      return updated;
    });
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleBudgetChange = (patch) =>
    setForm((prev) => ({ ...prev, ...patch }));

  const handleDateChange = (e) => {
    setForm((prev) => ({ ...prev, expectedClosingTime: e.target.value }));
    if (errors.expectedClosingTime)
      setErrors((prev) => ({ ...prev, expectedClosingTime: "" }));
  };

  const validate = () => {
    const e = {};
    if (step === 0) {
      if (!form.roleId) e.roleId = "Required";
      if (!form.jobTitle.trim()) e.jobTitle = "Required";
      if (!form.noOfVacancy || form.noOfVacancy < 1) e.noOfVacancy = "Min 1";
      if (!form.branchId) e.branchId = "Required";
      if (!form.departmentId) e.departmentId = "Required";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const next = () => {
    if (validate()) setStep((s) => s + 1);
  };
  const prev = () => setStep((s) => s - 1);

  const handleSubmit = async () => {
    if (!validate()) return;
    setLoading(true);
    try {
      const payload = {
        jobTitle: form.jobTitle,
        employmentType: form.employmentType?.toUpperCase().replace(" ", "_"),
        qualificationIds:
          form.qualificationIds.length > 0 ? form.qualificationIds : undefined,
        noOfVacancy: Number(form.noOfVacancy),
        budget: Number(form.budget) || 0,
        budgetType: form.budgetType,
        salaryType: form.salaryType,
        expectedClosingTime: form.expectedClosingTime
          ? new Date(form.expectedClosingTime).toISOString()
          : undefined,
        reasonOfVacancy: form.reasonOfVacancy || undefined,
        branchId: Number(form.branchId),
        departmentId: Number(form.departmentId),
        roleId: Number(form.roleId),
        jobDescription: form.jobDescription || undefined,
        responsibilities: form.responsibilities || undefined,
        desiredProfile: form.desiredProfile || undefined,
        experience: form.experience || undefined,
        skills: form.skills || undefined,
      };

      if (editMode && existingVacancy) {
        await axios.put(
          `${process.env.REACT_APP_API_MANAGER_URL}/recruitment/vacancies/basic_details/${existingVacancy.vacancyId}`,
          payload,
        );
      } else {
        await axios.post(
          `${process.env.REACT_APP_API_MANAGER_URL}/recruitment/vacancies`,
          payload,
        );
      }
      setSubmitted(true);
      onCreated();
    } catch (err) {
      toast.error(
        err?.response?.data?.message ||
          `Failed to ${editMode ? "update" : "submit"} vacancy.`,
      );
    } finally {
      setLoading(false);
    }
  };

  // ── Step 0: Vacancy Details ───────────────────────────────────────────────
  const step0 = (
    <>
      <div className="cv-grid cv-grid--2">
        <F label="Role" required error={errors.roleId}>
          <select
            className={`cv-field__input${
              errors.roleId ? " cv-field__input--error" : ""
            }`}
            name="roleId"
            value={form.roleId}
            onChange={handleChange}
            disabled={metaLoading}
          >
            <option value="">{metaLoading ? "Loading…" : "Select Role"}</option>
            {roles.map((r) => (
              <option key={r.roleId} value={r.roleId}>
                {r.roleName}
              </option>
            ))}
          </select>
        </F>
        <F
          label="Job Title"
          required
          error={errors.jobTitle}
          hint="(can be modified manually)"
        >
          <input
            className={`cv-field__input${
              errors.jobTitle ? " cv-field__input--error" : ""
            }`}
            name="jobTitle"
            value={form.jobTitle}
            onChange={handleChange}
            placeholder="e.g. Sales Executive"
          />
        </F>
      </div>

      <div className="cv-grid cv-grid--2">
        <F label="No. of Positions" required error={errors.noOfVacancy}>
          <input
            className={`cv-field__input${
              errors.noOfVacancy ? " cv-field__input--error" : ""
            }`}
            type="number"
            min="1"
            name="noOfVacancy"
            value={form.noOfVacancy}
            onChange={handleChange}
          />
        </F>
        <F label="Employment Type">
          <select
            className="cv-field__input"
            name="employmentType"
            value={form.employmentType}
            onChange={handleChange}
          >
            {EMPLOYMENT_TYPES.map(([v, l]) => (
              <option key={v} value={v}>
                {l}
              </option>
            ))}
          </select>
        </F>
      </div>

      <div className="cv-grid cv-grid--2">
        <F label="Branch" required error={errors.branchId}>
          {branchesLoading ? (
            <div className="cv-field__loading">Loading branches…</div>
          ) : (
            <select
              className={`cv-field__input${
                errors.branchId ? " cv-field__input--error" : ""
              }`}
              name="branchId"
              value={form.branchId}
              onChange={handleChange}
            >
              <option value="">Select Branch</option>
              {branches.map((b) => (
                <option key={b.branchId} value={b.branchId}>
                  {b.branchName}
                </option>
              ))}
            </select>
          )}
        </F>
        <F label="Department" required error={errors.departmentId}>
          <select
            className={`cv-field__input${
              errors.departmentId ? " cv-field__input--error" : ""
            }`}
            name="departmentId"
            value={form.departmentId}
            onChange={handleChange}
            disabled={metaLoading}
          >
            <option value="">
              {metaLoading ? "Loading…" : "Select Department"}
            </option>
            {departments.map((d) => (
              <option key={d.departmentId} value={d.departmentId}>
                {d.departmentName}
              </option>
            ))}
          </select>
        </F>
      </div>

      <F label="Budget - Upto/Max">
        <BudgetInput
          value={form.budget}
          display={form.budgetDisplay}
          budgetType={form.budgetType}
          salaryType={form.salaryType}
          onChange={handleBudgetChange}
        />
      </F>

      <F label="Expected Closing Date" hint="(optional)">
        <DateTimePicker
          name="expectedClosingTime"
          value={form.expectedClosingTime}
          onChange={handleDateChange}
          error={errors.expectedClosingTime}
        />
      </F>

      <F label="Reason for Vacancy" hint="(optional)">
        <textarea
          className="cv-field__input cv-field__textarea"
          name="reasonOfVacancy"
          value={form.reasonOfVacancy}
          onChange={handleChange}
          rows={3}
          placeholder="Enter reason for vacancy..."
        />
        <div className="cv-suggestions">
          {VACANCY_REASON_SUGGESTIONS.map((s) => (
            <button
              key={s}
              type="button"
              className="suggestion-chip"
              onClick={() =>
                setForm((p) => ({
                  ...p,
                  reasonOfVacancy: p.reasonOfVacancy
                    ? `${p.reasonOfVacancy}\n• ${s}`
                    : `• ${s}`,
                }))
              }
            >
              + {s}
            </button>
          ))}
        </div>
      </F>
    </>
  );

  // ── Step 1: Job Details ───────────────────────────────────────────────────
  const step1 = (
    <>
      <F
        label="Job Description"
        hint="(optional)"
        error={errors.jobDescription}
      >
        <textarea
          className="cv-field__input cv-field__textarea"
          name="jobDescription"
          value={form.jobDescription}
          onChange={handleChange}
          rows={4}
          placeholder="Enter job description..."
        />
        <div className="cv-suggestions">
          {JOB_DESCRIPTION_SUGGESTIONS.map((s) => (
            <button
              key={s}
              type="button"
              className="suggestion-chip"
              onClick={() =>
                setForm((p) => ({
                  ...p,
                  jobDescription: p.jobDescription
                    ? `${p.jobDescription}\n• ${s}`
                    : `• ${s}`,
                }))
              }
            >
              + {s}
            </button>
          ))}
        </div>
      </F>

      <div className="cv-field">
        <F label="Responsibilities">
          <textarea
            className="cv-field__input cv-field__textarea"
            name="responsibilities"
            value={form.responsibilities}
            onChange={handleChange}
            rows={3}
            placeholder="Enter key responsibilities..."
          />
          <div className="cv-suggestions">
            {RESPONSIBILITY_SUGGESTIONS.map((s) => (
              <button
                key={s}
                type="button"
                className="suggestion-chip"
                onClick={() =>
                  setForm((p) => ({
                    ...p,
                    responsibilities: p.responsibilities
                      ? `${p.responsibilities}\n• ${s}`
                      : `• ${s}`,
                  }))
                }
              >
                + {s}
              </button>
            ))}
          </div>
        </F>
      </div>

      <div className="cv-field">
        <F label="Desired Profile">
          <textarea
            className="cv-field__input cv-field__textarea"
            name="desiredProfile"
            value={form.desiredProfile}
            onChange={handleChange}
            rows={3}
            placeholder="Enter desired candidate profile..."
          />
          <div className="cv-suggestions">
            {DESIRED_PROFILE_SUGGESTIONS.map((s) => (
              <button
                key={s}
                type="button"
                className="suggestion-chip"
                onClick={() =>
                  setForm((p) => ({
                    ...p,
                    desiredProfile: p.desiredProfile
                      ? `${p.desiredProfile}\n• ${s}`
                      : `• ${s}`,
                  }))
                }
              >
                + {s}
              </button>
            ))}
          </div>
        </F>
      </div>

      <F label="Experience">
        <ExperienceCards value={form.experience} onChange={handleChange} />
      </F>

      <F label="Skills" hint="comma-separated">
        <input
          className="cv-field__input"
          name="skills"
          value={form.skills}
          onChange={handleChange}
          placeholder="e.g. Communication, Leadership, MS Excel"
          style={{ marginTop: 4 }}
        />
      </F>

      {/* ✅ Replaced qualificationType + qualification dropdowns */}
      <F label="Qualifications" hint="(optional)">
        <QualificationMultiSelectLocal
          qualifications={qualifications}
          qualificationsLoading={qualificationsLoading}
          selectedIds={form.qualificationIds}
          onChange={(ids) => setForm((p) => ({ ...p, qualificationIds: ids }))}
        />
      </F>
    </>
  );

  // ── Success screen ────────────────────────────────────────────────────────
  if (submitted) {
    return (
      <>
        <div className="drawer-backdrop" onClick={onClose} />
        <div className="drawer">
          <div className="drawer__header">
            <div>
              <h2 className="drawer__title">
                {editMode ? "Vacancy Updated" : "Vacancy Created"}
              </h2>
              <p className="drawer__sub">What would you like to do next?</p>
            </div>
            <button className="drawer__close" onClick={onClose}>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path
                  d="M5 5l10 10M15 5L5 15"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          </div>
          <div className="drawer__body">
            <div className="cv-success">
              <div className="cv-success__ring">
                <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
                  <path
                    d="M6 16l7 7 13-13"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <h3 className="cv-success__title">
                {editMode ? "Vacancy Updated!" : "Vacancy Submitted!"}
              </h3>
              <p className="cv-success__sub">
                {editMode
                  ? "Your vacancy has been updated successfully."
                  : "Your vacancy has been sent for HOD & HR approval. While it's being reviewed, go ahead and create a Job Post so candidates can apply."}
              </p>
              <span className="cv-success__next-label">What's next?</span>
              <div className="cv-success__shortcuts">
                {!editMode && (
                  <button
                    className="cv-success__shortcut cv-success__shortcut--primary"
                    onClick={() => {
                      onClose();
                      navigate("/recruitment/job-posts/create");
                    }}
                  >
                    <div className="cv-success__shortcut-icon">
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 20 20"
                        fill="none"
                      >
                        <rect
                          x="3"
                          y="3"
                          width="14"
                          height="14"
                          rx="2"
                          stroke="currentColor"
                          strokeWidth="1.6"
                        />
                        <path
                          d="M10 7v6M7 10h6"
                          stroke="currentColor"
                          strokeWidth="1.6"
                          strokeLinecap="round"
                        />
                      </svg>
                    </div>
                    <div className="cv-success__shortcut-body">
                      <div className="cv-success__shortcut-title">
                        Create a Job Post
                      </div>
                      <div className="cv-success__shortcut-desc">
                        Publish an opening so candidates can discover and apply
                      </div>
                    </div>
                    <svg
                      className="cv-success__shortcut-arrow"
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                    >
                      <path
                        d="M4 8h8M9 5l3 3-3 3"
                        stroke="currentColor"
                        strokeWidth="1.6"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                )}
                <button className="cv-success__shortcut" onClick={onClose}>
                  <div className="cv-success__shortcut-icon">
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <path
                        d="M3 5h14M3 10h14M3 15h8"
                        stroke="currentColor"
                        strokeWidth="1.6"
                        strokeLinecap="round"
                      />
                    </svg>
                  </div>
                  <div className="cv-success__shortcut-body">
                    <div className="cv-success__shortcut-title">
                      View My Vacancies
                    </div>
                    <div className="cv-success__shortcut-desc">
                      Track the approval status of your requests
                    </div>
                  </div>
                  <svg
                    className="cv-success__shortcut-arrow"
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                  >
                    <path
                      d="M4 8h8M9 5l3 3-3 3"
                      stroke="currentColor"
                      strokeWidth="1.6"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
                {!editMode && (
                  <button
                    className="cv-success__shortcut"
                    onClick={() => {
                      setForm(initialForm);
                      setStep(0);
                      setErrors({});
                      setSubmitted(false);
                    }}
                  >
                    <div className="cv-success__shortcut-icon">
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 20 20"
                        fill="none"
                      >
                        <circle
                          cx="10"
                          cy="10"
                          r="7"
                          stroke="currentColor"
                          strokeWidth="1.6"
                        />
                        <path
                          d="M10 7v6M7 10h6"
                          stroke="currentColor"
                          strokeWidth="1.6"
                          strokeLinecap="round"
                        />
                      </svg>
                    </div>
                    <div className="cv-success__shortcut-body">
                      <div className="cv-success__shortcut-title">
                        Create Another Vacancy
                      </div>
                      <div className="cv-success__shortcut-desc">
                        Start a new vacancy request from scratch
                      </div>
                    </div>
                    <svg
                      className="cv-success__shortcut-arrow"
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                    >
                      <path
                        d="M4 8h8M9 5l3 3-3 3"
                        stroke="currentColor"
                        strokeWidth="1.6"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="drawer-backdrop" onClick={onClose} />
      <div className="drawer">
        <div className="drawer__header">
          <div>
            <h2 className="drawer__title">
              {editMode
                ? "Edit Vacancy"
                : "Create Vacancy (Man power requisition form)"}
            </h2>
            <p className="drawer__sub">
              Step {step + 1} of {STEPS.length} — {STEPS[step]}
            </p>
          </div>
          <button className="drawer__close" onClick={onClose}>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path
                d="M5 5l10 10M15 5L5 15"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>
        <div className="drawer__stepper">
          {STEPS.map((label, i) => (
            <div
              key={label}
              className={`drawer__step${
                i === step ? " drawer__step--active" : ""
              }${i < step ? " drawer__step--done" : ""}`}
            >
              <div className="drawer__step-dot" />
              <span>{label}</span>
            </div>
          ))}
        </div>
        <div className="drawer__body">{step === 0 ? step0 : step1}</div>
        <div className="drawer__footer">
          {step > 0 ? (
            <button className="cv-btn cv-btn--ghost" onClick={prev}>
              ← Back
            </button>
          ) : (
            <button className="cv-btn cv-btn--ghost" onClick={onClose}>
              Cancel
            </button>
          )}
          <div style={{ flex: 1 }} />
          {step < STEPS.length - 1 ? (
            <button className="cv-btn cv-btn--primary" onClick={next}>
              Continue →
            </button>
          ) : (
            <button
              className="cv-btn cv-btn--submit"
              onClick={handleSubmit}
              disabled={loading}
            >
              {loading ? (
                <span className="cv-btn__spinner" />
              ) : editMode ? (
                "Save Changes"
              ) : (
                "Submit for Approval"
              )}
            </button>
          )}
        </div>
      </div>
    </>
  );
};

export default AllVacancies;
